import * as React from "react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarRange,
  ArrowRight,
  Info,
} from "lucide-react";
import {
  DayButton,
  DayPicker,
  getDefaultClassNames,
  type DateRange,
} from "react-day-picker";
import { format, addMonths, subMonths } from "date-fns";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Chip, ChipGroup } from "@/components/ui/chip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  formatters,
  components,
  activeChipIndex,
  ...props
}: React.ComponentProps<typeof DayPicker> & { activeChipIndex?: number }) {
  const defaultClassNames = getDefaultClassNames();

  // Prevent focus on navigation buttons when calendar opens
  React.useEffect(() => {
    const preventNavigationFocus = () => {
      const navButtons = document.querySelectorAll(".wl-calendar-nav button");
      navButtons.forEach((button) => {
        button.setAttribute("tabIndex", "-1");
      });
    };

    // Run immediately and after a short delay to catch any dynamically rendered buttons
    preventNavigationFocus();
    const timeoutId = setTimeout(preventNavigationFocus, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  // Prevent any auto-focus behavior from react-day-picker
  React.useEffect(() => {
    const preventAutoFocus = () => {
      // Remove focus from any focused calendar day buttons
      const focusedButtons = document.querySelectorAll(
        ".wl-calendar-day-button:focus"
      );
      focusedButtons.forEach((button) => {
        if (button instanceof HTMLElement) {
          button.blur();
        }
      });

      // Remove focus from today's date if it gets focused
      const todayButton = document.querySelector(
        ".wl-calendar-day-today .wl-calendar-day-button"
      );
      if (todayButton instanceof HTMLElement) {
        todayButton.blur();
      }
    };

    // Run after a delay to catch any auto-focus that happens
    const timeoutId = setTimeout(preventAutoFocus, 50);
    const timeoutId2 = setTimeout(preventAutoFocus, 150);
    const timeoutId3 = setTimeout(preventAutoFocus, 300);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
    };
  }, []);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("wl-calendar", className)}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("wl-calendar-root", defaultClassNames.root),
        months: cn("wl-calendar-months", defaultClassNames.months),
        month: cn("wl-calendar-month", defaultClassNames.month),
        nav: cn("wl-calendar-nav", defaultClassNames.nav),
        //  wl-calendar-nav-button-previous
        button_previous: cn(
          "wl-btn wl-btn-primary-text",
          defaultClassNames.button_previous
        ),
        //  wl-calendar-nav-button-next
        button_next: cn(
          "wl-btn wl-btn-primary-text",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "wl-calendar-caption",
          defaultClassNames.month_caption
        ),
        dropdowns: cn("wl-calendar-dropdowns", defaultClassNames.dropdowns),
        dropdown_root: cn(
          "wl-calendar-dropdown-root",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn("wl-calendar-dropdown", defaultClassNames.dropdown),
        caption_label: cn(
          "wl-calendar-caption-label",
          defaultClassNames.caption_label
        ),
        table: "wl-calendar-table",
        weekdays: cn("wl-calendar-weekdays", defaultClassNames.weekdays),
        weekday: cn("wl-calendar-weekday", defaultClassNames.weekday),
        week: cn("wl-calendar-week", defaultClassNames.week),
        week_number_header: cn(
          "wl-calendar-week-number-header",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "wl-calendar-week-number",
          defaultClassNames.week_number
        ),
        day: cn("wl-calendar-day", defaultClassNames.day),
        range_start: cn(
          "wl-calendar-range-start",
          defaultClassNames.range_start
        ),
        range_middle: cn(
          "wl-calendar-range-middle",
          defaultClassNames.range_middle
        ),
        range_end: cn("wl-calendar-range-end", defaultClassNames.range_end),
        today: cn("wl-calendar-day-today", defaultClassNames.today),
        outside: cn("wl-calendar-day-outside", defaultClassNames.outside),
        disabled: cn("wl-calendar-day-disabled", defaultClassNames.disabled),
        hidden: cn("wl-calendar-day-hidden", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon
                className={cn("wl-calendar-icon", className)}
                tabIndex={-1}
                {...props}
              />
            );
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("wl-calendar-icon", className)}
                tabIndex={-1}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon
              className={cn("wl-calendar-icon", className)}
              tabIndex={-1}
              {...props}
            />
          );
        },
        DayButton: (props) => (
          <CalendarDayButton {...props} activeChipIndex={activeChipIndex} />
        ),
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="wl-calendar-week-number-cell">{children}</div>
            </td>
          );
        },
        ...components,
      }}
      // Disable initial focus completely
      initialFocus={false}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  activeChipIndex,
  ...props
}: React.ComponentProps<typeof DayButton> & { activeChipIndex?: number }) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <Button
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-today={modifiers.today}
      data-current-date={modifiers.today ? "true" : "false"}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn("wl-calendar-day-button", defaultClassNames.day, className)}
      disabled={modifiers.outside || modifiers.disabled}
      variant="text"
      // Prevent focus styling
      onFocus={(e) => {
        e.currentTarget.blur();
      }}
      {...props}
    >
      {day.date.getDate()}
      {modifiers.today && (
        <div
          className={cn(
            "wl-calendar-tag",
            activeChipIndex === 0 && "wl-range-today"
          )}
        />
      )}
    </Button>
  );
}

// DateRangePicker component with automatic month navigation
interface DateRangePickerProps {
  dateRange?: { from: Date | undefined; to?: Date | undefined };
  onDateRangeChange?: (
    range: { from: Date | undefined; to?: Date | undefined } | undefined
  ) => void;
  placeholder?: string;
  className?: string;
  showQuickFilters?: boolean;
  showSaveOption?: boolean;
  onSave?: (
    range: { from: Date | undefined; to?: Date | undefined } | undefined,
    saveFilter: boolean
  ) => void;
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  placeholder = "Select date range",
  className,
  showQuickFilters = true,
  showSaveOption = true,
  onSave,
}: DateRangePickerProps) {
  const [firstCalendarMonth, setFirstCalendarMonth] = React.useState<Date>(
    new Date()
  );
  const [secondCalendarMonth, setSecondCalendarMonth] = React.useState<Date>(
    addMonths(new Date(), 1)
  );
  const [saveFilter, setSaveFilter] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeChipIndex, setActiveChipIndex] = React.useState(0);

  // Initialize calendar months when dateRange changes
  React.useEffect(() => {
    if (dateRange?.from) {
      setFirstCalendarMonth(dateRange.from);
      setSecondCalendarMonth(addMonths(dateRange.from, 1));
    } else {
      setFirstCalendarMonth(new Date());
      setSecondCalendarMonth(addMonths(new Date(), 1));
    }
  }, [dateRange]);

  // Debug: Log when activeChipIndex changes
  React.useEffect(() => {
    console.log("Active chip index changed to:", activeChipIndex);
  }, [activeChipIndex]);

  // Handle month navigation for first calendar
  const handleFirstCalendarMonthChange = (month: Date) => {
    setFirstCalendarMonth(month);
    setSecondCalendarMonth(addMonths(month, 1));
  };

  // Handle month navigation for second calendar
  const handleSecondCalendarMonthChange = (month: Date) => {
    setSecondCalendarMonth(month);
    setFirstCalendarMonth(subMonths(month, 1));
  };

  // Adapter function to convert react-day-picker DateRange to our format
  const handleDateRangeSelect = (range: DateRange | undefined) => {
    if (range?.from) {
      onDateRangeChange?.({ from: range.from, to: range.to || undefined });
      // Switch to Custom chip when user selects dates
      setActiveChipIndex(13);
    } else {
      onDateRangeChange?.(undefined);
      // Also switch to Custom chip when clearing selection
      setActiveChipIndex(13);
    }
  };

  // Handle chip group changes
  const handleChipGroupChange = (groupId: string, chipIndex: number) => {
    setActiveChipIndex(chipIndex);

    // Apply filter based on chip index
    const filterMap: { [key: number]: string } = {
      0: "today",
      1: "yesterday",
      2: "this-week",
      3: "last-week",
      4: "this-month",
      5: "last-month",
      6: "last-3-months",
      7: "1st-half",
      8: "2nd-half",
      9: "q1",
      10: "q2",
      11: "q3",
      12: "q4",
      // 13 is Custom - no filter to apply
    };

    const filterType = filterMap[chipIndex];
    if (filterType) {
      applyQuickFilter(filterType);
    }
  };

  // Quick filter functions
  const applyQuickFilter = (filterType: string) => {
    const today = new Date();
    let from: Date, to: Date;

    switch (filterType) {
      case "today":
        from = today;
        to = today;
        break;
      case "yesterday":
        from = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        to = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        break;
      case "this-week":
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        from = startOfWeek;
        to = endOfWeek;
        break;
      case "last-week":
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        from = lastWeekStart;
        to = lastWeekEnd;
        break;
      case "this-month":
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case "last-month":
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "last-3-months":
        from = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case "1st-half":
        from = new Date(today.getFullYear(), 0, 1);
        to = new Date(today.getFullYear(), 5, 30);
        break;
      case "2nd-half":
        from = new Date(today.getFullYear(), 6, 1);
        to = new Date(today.getFullYear(), 11, 31);
        break;
      case "q1":
        from = new Date(today.getFullYear(), 0, 1);
        to = new Date(today.getFullYear(), 2, 31);
        break;
      case "q2":
        from = new Date(today.getFullYear(), 3, 1);
        to = new Date(today.getFullYear(), 5, 30);
        break;
      case "q3":
        from = new Date(today.getFullYear(), 6, 1);
        to = new Date(today.getFullYear(), 8, 30);
        break;
      case "q4":
        from = new Date(today.getFullYear(), 9, 1);
        to = new Date(today.getFullYear(), 11, 31);
        break;
      default:
        return;
    }

    onDateRangeChange?.({ from, to });
  };

  const clearSelection = () => {
    onDateRangeChange?.(undefined);
    setFirstCalendarMonth(new Date());
    setSecondCalendarMonth(addMonths(new Date(), 1));
    setActiveChipIndex(0); // Reset to Today
  };

  const handleSave = () => {
    onSave?.(dateRange, saveFilter);
    setActiveChipIndex(13); // Set Custom chip as active
    setIsOpen(false); // Close the popover
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "wl-width-240 wl-d-flex wl-justify-content-between wl-align-items-center wl-gap-8",
            className
          )}
        >
          {dateRange?.from ? (
            dateRange.to ? (
              <>
                {format(dateRange.from, "dd MMM yyyy")} -{" "}
                {format(dateRange.to, "dd MMM yyyy")}
              </>
            ) : (
              format(dateRange.from, "dd MMM yyyy")
            )
          ) : (
            placeholder
          )}
          <CalendarRange className="wl-mr-2 wl-h-4 wl-w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        type="arrow-none"
        position="bottom-left"
        className="wl-w-auto wl-p-0"
        align="start"
      >
        <div className="wl-p-3">
          {showQuickFilters && (
            <div className="wl-mb-3 wl-max-width-656">
              <ChipGroup
                className="wl-w-100 wl-d-flex wl-justify-content-center wl-align-items-center wl-flex-wrap wl-gap-8"
                groupId="date-filter-group"
                defaultActiveChip={0}
                activeChip={activeChipIndex}
                onGroupChange={handleChipGroupChange}
                key={activeChipIndex}
              >
                <Chip shape="rounded">Today</Chip>
                <Chip shape="rounded">Yesterday</Chip>
                <Chip shape="rounded">This week</Chip>
                <Chip shape="rounded">Last week</Chip>
                <Chip shape="rounded">This month</Chip>
                <Chip shape="rounded">Last month</Chip>
                <Chip shape="rounded">Last 3 months</Chip>
                <Chip shape="rounded">1st half</Chip>
                <Chip shape="rounded">2nd half</Chip>
                <Chip shape="rounded">Q1</Chip>
                <Chip shape="rounded">Q2</Chip>
                <Chip shape="rounded">Q3</Chip>
                <Chip shape="rounded">Q4</Chip>
                <div className="wl-date-filter-divider" />
                <Chip shape="rounded">Custom</Chip>
              </ChipGroup>
            </div>
          )}

          <div className="wl-d-flex wl-gap-4">
            <div className="wl-flex-1">
              <Calendar
                mode="range"
                month={firstCalendarMonth}
                onMonthChange={handleFirstCalendarMonthChange}
                selected={dateRange}
                onSelect={handleDateRangeSelect}
                numberOfMonths={1}
                className="wl-border-0"
                activeChipIndex={activeChipIndex}
              />
            </div>
            <div className="wl-d-flex wl-justify-content-center wl-align-items-center">
              <ArrowRight size={24} className="wl-fg-secondary" />
            </div>
            <div className="wl-flex-1">
              <Calendar
                mode="range"
                month={secondCalendarMonth}
                onMonthChange={handleSecondCalendarMonthChange}
                selected={dateRange}
                onSelect={handleDateRangeSelect}
                numberOfMonths={1}
                className="wl-border-0"
                activeChipIndex={activeChipIndex}
              />
            </div>
          </div>

          {showSaveOption && (
            <div className=" wl-w-100 wl-d-flex wl-justify-content-between wl-align-items-center wl-mt-12">
              <div className="wl-d-flex wl-align-items-center wl-gap-4">
                <div className="wl-d-flex">
                  <Checkbox
                    id="save-filter"
                    checked={saveFilter}
                    onCheckedChange={(checked) =>
                      setSaveFilter(checked as boolean)
                    }
                  />
                  <Label
                    htmlFor="save-filter"
                    className="wl-ps-1 wl-text-button-medium"
                  >
                    Save this date filtering for all time
                  </Label>
                </div>
                <Info size={16} className="wl-fg-secondary" />
              </div>
              <div className="wl-d-flex wl-align-items-center wl-gap-8">
                <Button variant="text" onClick={clearSelection}>
                  Clear
                </Button>
                <Button variant="primary" onClick={handleSave}>
                  Save
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { Calendar, CalendarDayButton };
